import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { PRINCIPLES, PROFILE } from "@/data/site";
import { MaskLines, Reveal, SectionLabel } from "@/components/Primitives";

const InfoPanel = () => (
  <Reveal delay={0.15} className="relative border border-line bg-coal p-8 sm:p-10" data-testid="about-info-panel">
    <div className="mb-8">
      <span className="eyebrow">Profile</span>
    </div>
    <dl className="divide-y divide-line">
      {PROFILE.details.map((d) => (
        <div key={d.label} className="grid grid-cols-3 gap-4 py-5" data-testid={`about-info-${d.label.toLowerCase()}`}>
          <dt className="font-mono text-xs uppercase tracking-[0.2em] text-ash">{d.label}</dt>
          <dd className="col-span-2 font-serif text-xl leading-snug text-ivory">{d.value}</dd>
        </div>
      ))}
    </dl>
  </Reveal>
);

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="about" ref={ref} className="py-28 sm:py-36 lg:py-44" data-testid="about-section">
      <div className="container-x grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <SectionLabel index="01" label="About" />
          <MaskLines lines={["More Than", "Just Code."]} className="mt-8 font-serif text-5xl font-light leading-[1] text-ivory sm:text-6xl lg:text-7xl" />
          <Reveal as="p" delay={0.1} className="mt-10 max-w-2xl text-base leading-relaxed text-ash sm:text-lg">
            I'm Hades, a developer passionate about building digital products that combine functionality, performance, and thoughtful design. I enjoy turning ideas into practical solutions, exploring new technologies, and creating projects that stand out through their quality and attention to detail.
          </Reveal>
          <ul className="mt-16 border-t border-line">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="li" key={p.title} delay={i * 0.08} className="group grid grid-cols-12 items-baseline gap-4 border-b border-line py-6" data-testid={`about-principle-${p.title.toLowerCase()}`}>
                <span className="col-span-2 font-mono text-xs text-gold sm:col-span-1">0{i + 1}</span>
                <span className="col-span-10 font-serif text-3xl text-ivory transition-transform duration-500 group-hover:translate-x-2 sm:col-span-5">{p.title}</span>
                <span className="col-span-10 col-start-3 text-ash sm:col-span-6 sm:col-start-auto">{p.text}</span>
              </Reveal>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-8 lg:col-span-4 lg:col-start-9">
          <Reveal className="relative aspect-[4/5] overflow-hidden border border-line">
            <motion.img
              src="/images/stairs.jpg"
              alt="Dark architectural staircase in shadow"
              loading="lazy"
              style={{ y: imgY, scale: 1.18 }}
              className="h-full w-full object-cover grayscale"
            />
          </Reveal>
          <InfoPanel />
        </div>
      </div>
    </section>
  );
};
